import type { Asset, DocumentFont, Rect, ShaderDef } from '@flobi/artwork-core';
import { documentFontUrls } from '../canvas/documentFonts';
import { rasterDimensions } from './rasterExportLimits';
import { readableMediaUrl } from '../services/mediaRead';

export interface RasterTarget { name: string; box: Rect; ids?: string[] }
export interface RasterSnapshot {
  page: Uint8Array;
  shaders: [string, ShaderDef][];
  assets: [string, Asset & { frame?: Uint8Array }][];
  /** Each sketch layer's picture at rest, by layer. */
  sketchFrames?: [string, Uint8Array][];
  fonts: DocumentFont[];
  proof?: { lut: Uint8Array; size: number };
  now: number;
  targets: RasterTarget[];
  scale: number;
  format: 'png' | 'jpg';
}
export type RasterRequest = Omit<RasterSnapshot, 'fonts'> & { fonts: { family: string; url: string }[] };
export type RasterReply = { type: 'progress'; message: string } | { type: 'done'; bytes: Uint8Array } | { type: 'error'; message: string };
export interface RasterOptions {
  scale: number;
  format: 'png' | 'jpg';
  selection?: boolean;
  /** Stable object snapshots for AI references, independent of later selection. */
  nodeIds?: readonly string[];
  targets: { artboardId?: string; name: string }[];
  signal: AbortSignal;
  onProgress: (message: string) => void;
}

/** The worker owns all expensive drawing, encoding and ZIP work. Termination also cancels a busy WASM call. */
export async function runRasterExport(snapshot: RasterSnapshot, signal: AbortSignal, onProgress: (message: string) => void): Promise<Uint8Array> {
  snapshot.targets.forEach(target => rasterDimensions(target.box, snapshot.scale));
  signal.throwIfAborted();
  onProgress('Preparing export…');
  const fonts = await withAbort(Promise.all(snapshot.fonts.flatMap(font => (font.faces ?? []).map(async face => {
    if (face.format === 'woff' || face.format === 'woff2') throw new Error(`The font ${font.family} needs a TTF or OTF file before exporting.`);
    const { url } = await documentFontUrls.urlFor(font, face);
    return { family: font.family, url };
  }))), signal);
  const assets = await withAbort(Promise.all(snapshot.assets.map(async ([id, asset]): Promise<RasterSnapshot['assets'][number]> => [
    id, asset.src && !asset.frame ? { ...asset, src: await readableMediaUrl(asset.src, asset.mediaAssetId ?? id) } : asset,
  ])), signal);
  signal.throwIfAborted();
  return renderInWorker({ ...snapshot, assets, fonts }, signal, onProgress);
}

function withAbort<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Export cancelled.', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    work.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    if (signal.aborted) abort();
  });
}

/** Exported separately so cancellation, failures, timeout and cleanup are testable without CanvasKit. */
export function renderInWorker(request: RasterRequest, signal: AbortSignal, onProgress: (message: string) => void,
  createWorker = () => new Worker(new URL('./rasterExport.worker.ts', import.meta.url), { type: 'module' }),
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted();
    const worker = createWorker();
    let settled = false;
    const finish = (error?: Error, bytes?: Uint8Array) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      worker.terminate();
      if (error) reject(error); else resolve(bytes!);
    };
    const abort = () => finish(new DOMException('Export cancelled.', 'AbortError'));
    const timer = setTimeout(() => finish(new Error('Export took too long. Try a smaller size or fewer effects.')), 120_000);
    signal.addEventListener('abort', abort, { once: true });
    worker.onmessage = (event: MessageEvent<RasterReply>) => {
      if (settled) return;
      if (event.data.type === 'progress') onProgress(event.data.message);
      else if (event.data.type === 'error') finish(new Error(event.data.message));
      else finish(undefined, event.data.bytes);
    };
    worker.onerror = event => { event.preventDefault(); finish(new Error('The export renderer stopped. Try a smaller size.')); };
    worker.onmessageerror = () => finish(new Error('The export could not be read. Please try again.'));
    try { worker.postMessage(request); } catch (error) { finish(error as Error); }
  });
}
